import { useState, useRef, useEffect } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Palette } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  className?: string;
  disabled?: boolean;
}

export function ColorPicker({ value, onChange, className, disabled }: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync input value with prop value
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Validate and normalize hex color
  const isValidHex = (hex: string): boolean => {
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex);
  };

  const normalizeHex = (hex: string): string => {
    if (!hex.startsWith('#')) {
      hex = '#' + hex;
    }
    // Convert 3-digit hex to 6-digit
    if (hex.length === 4) {
      hex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
    }
    return hex.toLowerCase();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Validate and update color
    if (isValidHex(newValue)) {
      const normalizedHex = normalizeHex(newValue);
      onChange(normalizedHex);
    }
  };

  const handleInputBlur = () => {
    // On blur, ensure input shows current valid value
    if (!isValidHex(inputValue)) {
      setInputValue(value);
    }
  };

  const handleColorPickerChange = (color: string) => {
    const normalizedColor = normalizeHex(color);
    setInputValue(normalizedColor);
    onChange(normalizedColor);
  };

  const handleSwatchClick = () => {
    if (!disabled) {
      setOpen(true);
    }
  };

  return (
    <div className={cn("flex space-x-2", className)}>
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        placeholder="#000000"
        className="flex-1 font-mono"
        disabled={disabled}
        maxLength={7}
      />
      
      <div className="flex space-x-1">
        {/* Color Swatch */}
        <button
          type="button"
          onClick={handleSwatchClick}
          disabled={disabled}
          className={cn(
            "w-10 h-10 rounded border border-gray-300 flex-shrink-0 transition-all",
            !disabled && "hover:scale-105 cursor-pointer",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          style={{ backgroundColor: isValidHex(value) ? value : '#ffffff' }}
          title="Click to open color picker"
        />
        
        {/* Picker Button */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={disabled}
              className="w-10 h-10 flex-shrink-0"
            >
              <Palette className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          
          <DialogContent className="sm:max-w-md">
            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="text-lg font-medium">Choose Color</h3>
                <HexColorPicker
                  color={isValidHex(value) ? value : '#000000'}
                  onChange={handleColorPickerChange}
                  style={{ width: '100%' }}
                />
              </div>
              
              {/* Current Color Display */}
              <div className="flex items-center space-x-2">
                <div
                  className="w-8 h-8 rounded border border-gray-300"
                  style={{ backgroundColor: value }}
                />
                <Input
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  placeholder="#000000"
                  className="flex-1 font-mono"
                  maxLength={7}
                />
              </div>
              
              {/* Preset Colors */}
              <div>
                <p className="text-sm font-medium mb-2">Quick Colors</p>
                <div className="grid grid-cols-9 gap-2">
                  {[
                    '#000000', '#ffffff', '#ef4444', '#f97316', '#f59e0b', '#eab308',
                    '#84cc16', '#22c55e', '#10b981', '#06b6d4', '#0ea5e9', '#3b82f6',
                    '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e'
                  ].map((presetColor) => (
                    <button
                      key={presetColor}
                      type="button"
                      onClick={() => handleColorPickerChange(presetColor)}
                      className={cn(
                        "w-8 h-8 rounded border border-gray-200 transition-transform hover:scale-110",
                        value === presetColor && "ring-2 ring-blue-500 ring-offset-1"
                      )}
                      style={{ backgroundColor: presetColor }}
                      title={presetColor}
                    />
                  ))}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}